import ContentChangeEvent from '../events/ContentChangeEvent';
import SelectionChangeEvent from '../events/SelectionChangeEvent';
import BlurEvent from '../events/BlurEvent';
import FocusEvent from '../events/FocusEvent';
import { IRichTextFieldConverters, IRichTextFieldOptionsInternal } from '../RichTextField/IRichTextFieldOptions';
import IRichTextField from '../IRichTextField';
import { convertDeltaToFields } from './converters/convertDeltaToFields';
import { convertFieldsToDelta } from './converters/convertFieldsToDelta';
import { TextAreaContent } from './converters/types';
import Quill from './Quill';
import { IDelta, ISelectionRange, ITextAttributes } from './types';
import { keyboardBindings } from './keyboardBindings';
import {
    CDIFtoQuillAttributeMapping,
    DEFAULT_STYLE_VALUES,
    QuilltoCDIFAttributeMapping,
    StyleAttributes,
} from './converters/StyleAttributes';
import { cachedRichTextFields } from './formats/paragraph';

function preventDefault(e: DragEvent) {
    e.preventDefault();
}

function convertQuillValueToRTF(attribute: string, value: string) {
    if (attribute === 'list' && value === 'bullet') {
        return 'unordered';
    }

    if (QuilltoCDIFAttributeMapping[attribute]) {
        if (value === StyleAttributes[attribute]) {
            return true;
        }
        return false;
    }

    return value;
}

function convertRTFValueToQuill(attribute: string, value: any) {
    if (attribute === 'list' && value === 'unordered') {
        return 'bullet';
    }

    if (CDIFtoQuillAttributeMapping[attribute]) {
        if (value) {
            return StyleAttributes[CDIFtoQuillAttributeMapping[attribute]];
        }
        return DEFAULT_STYLE_VALUES[CDIFtoQuillAttributeMapping[attribute]];
    }

    return value;
}

function mapQuillAttributesToRTF(inputFormats: Record<string, any>) {
    const formats = { ...inputFormats };
    if (formats.list && formats.list === 'bullet') {
        formats.list = 'unordered';
    }

    if (formats.fontWeight) {
        formats.bold = convertQuillValueToRTF('fontWeight', formats.fontWeight);
        delete formats.fontWeight;
    }

    if (formats.fontStyle) {
        formats.italic = convertQuillValueToRTF('fontStyle', formats.fontStyle);
        delete formats.fontStyle;
    }

    if (formats.textDecoration) {
        formats.underline = convertQuillValueToRTF('textDecoration', formats.textDecoration);
        delete formats.textDecoration;
    }

    return formats;
}

function mapAllAttributesToRTF(inputFormats: Record<string, any[]>) {
    const formats = { ...inputFormats };

    if (formats.list) {
        formats.list = formats.list.map((value: string) => convertQuillValueToRTF('list', value));
    }

    if (formats.fontWeight) {
        formats.bold = formats.fontWeight.map((value) => convertQuillValueToRTF('fontWeight', value));
        delete formats.fontWeight;
    }

    if (formats.fontStyle) {
        formats.italic = formats.fontStyle.map((value) => convertQuillValueToRTF('fontStyle', value));
        delete formats.fontStyle;
    }

    if (formats.textDecoration) {
        formats.underline = formats.textDecoration.map((value) => convertQuillValueToRTF('textDecoration', value));
        delete formats.textDecoration;
    }

    return formats;
}

function setAttributeFromBlock(formats: Record<string, any>) {
    const newFormats = { ...formats };

    if (!newFormats.color && newFormats.blockColor != null) {
        newFormats.color = newFormats.blockColor;
    }
    if (!newFormats.fontSize && newFormats.blockFontSize != null) {
        newFormats.fontSize = newFormats.blockFontSize;
    }
    if (!newFormats.fontFamily && newFormats.blockFontFamily != null) {
        newFormats.fontFamily = newFormats.blockFontFamily;
    }
    if (!newFormats.casing && newFormats.blockCasing != null) {
        newFormats.casing = newFormats.blockCasing;
    }
    if (!newFormats.lineHeight && newFormats.blockLineHeight != null) {
        newFormats.lineHeight = newFormats.blockLineHeight;
    }
    if (!newFormats.letterspacing && newFormats.blockLetterspacing != null) {
        newFormats.letterspacing = newFormats.blockLetterspacing;
    }
    if (!newFormats.fontWeight && newFormats.blockFontWeight != null) {
        newFormats.fontWeight = newFormats.blockFontWeight;
    }
    if (!newFormats.fontStyle && newFormats.blockFontStyle != null) {
        newFormats.fontStyle = newFormats.blockFontStyle;
    }
    if (!newFormats.textDecoration && newFormats.blockTextDecoration != null) {
        newFormats.textDecoration = newFormats.blockTextDecoration;
    }
    if (!newFormats.strokeColor && newFormats.blockStrokeColor != null) {
        newFormats.strokeColor = newFormats.blockStrokeColor;
    }
    if (!newFormats.strokeOverprints && newFormats.blockStrokeOverprints != null) {
        newFormats.strokeOverprints = newFormats.blockStrokeOverprints;
    }
    if (!newFormats.strokeThickness && newFormats.blockStrokeThickness != null) {
        newFormats.strokeThickness = newFormats.blockStrokeThickness;
    }
    if (!newFormats.strokePosition && newFormats.blockStrokePosition != null) {
        newFormats.strokePosition = newFormats.blockStrokePosition;
    }
    if (!newFormats.overprints && newFormats.blockOverprints != null) {
        newFormats.overprints = newFormats.blockOverprints;
    }

    delete newFormats.blockColor;
    delete newFormats.blockFontSize;
    delete newFormats.blockFontFamily;
    delete newFormats.blockCasing;
    delete newFormats.blockLineHeight;
    delete newFormats.blockLetterspacing;
    delete newFormats.blockFontWeight;
    delete newFormats.blockFontStyle;
    delete newFormats.blockTextDecoration;
    delete newFormats.blockStrokeColor;
    delete newFormats.blockStrokeOverprints;
    delete newFormats.blockStrokeThickness;
    delete newFormats.blockStrokePosition;
    delete newFormats.blockOverprints;

    return newFormats;
}

export default class QuillImplementation extends IRichTextField {
    private options: IRichTextFieldOptionsInternal;
    private quill: any;
    private el: HTMLElement;

    private static convertColorFromRichText: (color: string) => any = (value: string) => value;

    /**
     * Quill formats have no way of using code contextual to the instance due to their
     * module pattern being global. So this static function allows setting the converters
     * needed on the formats
     * @param richTextFieldConverters - Partial converters object to set
     */
    static setConverters(richTextFieldConverters: Partial<IRichTextFieldConverters>) {
        if (richTextFieldConverters.color) {
            const color = Quill.import('formats/color');
            color.converter = richTextFieldConverters.color.toRichText;

            const overprints = Quill.import('formats/overprints');
            overprints.converter = richTextFieldConverters.color.toRichText;

            const blockColor = Quill.import('formats/blockColor');
            blockColor.converter = richTextFieldConverters.color.toRichText;

            const blockOverprints = Quill.import('formats/blockOverprints');
            blockOverprints.converter = richTextFieldConverters.color.toRichText;

            QuillImplementation.convertColorFromRichText = richTextFieldConverters.color.fromRichText;
        }

        if (richTextFieldConverters.size) {
            const size = Quill.import('formats/fontSize');
            size.converter = richTextFieldConverters.size.toRichText;

            const blockSize = Quill.import('formats/blockFontSize');
            blockSize.converter = richTextFieldConverters.size.toRichText;
        }
    }

    constructor(element: HTMLElement, options: IRichTextFieldOptionsInternal) {
        super();
        this.options = options;
        this.el = element;
        this.quill = new Quill(this.el, {
            modules: {
                keyboard: {
                    bindings: keyboardBindings,
                },
            },
        });

        // Fix for android IME new line not moving cursor
        const quillInstance = this.quill;
        this.quill.on('editor-change', (eventName, ...args) => {
            if (eventName === 'text-change') {
                // args[0] will be delta
                const { ops } = args[0];
                const oldSelection = quillInstance.getSelection();
                if (!oldSelection) {
                    return;
                }
                const oldPos = oldSelection.index;
                const oldSelectionLength = oldSelection.length;

                // eslint-disable-next-line max-len
                if (ops[0].retain === undefined || !ops[1] || !ops[1].insert || !ops[1].insert || ops[1].insert !== '\n' || oldSelectionLength > 0) {
                    return;
                }

                setTimeout(() => {
                    const newPos = quillInstance.getSelection().index;
                    if (newPos === oldPos) {
                        quillInstance.setSelection(quillInstance.getSelection().index + 1, 0);
                    }
                }, 30);
            }
        });

        this.quill.on(Quill.events.SELECTION_CHANGE, this.dispatchSelectionChangeEvent);
        this.quill.on(Quill.events.TEXT_CHANGE, this.dispatchContentChangeEvent);
        this.quill.on(Quill.events.SCROLL_BEFORE_UPDATE, (source, mutations) => {
            for (let i = 0; i < mutations.length; i++) {
                // Mutation of the aria-hidden attribute should be ignored so it doesn't trigger unnecessary content-change events
                if (mutations[i].attributeName === 'aria-hidden') {
                    mutations.splice(i, 1);
                }
            }
        });
        this.quill.on(Quill.events.SCROLL_OPTIMIZE, () => {
            // wrap this code in a timeout, otherwise the event fires in the middle of an optimize cycle and
            // the range gets reported incorrectly
            setTimeout(() => {
                const [range] = this.quill.selection.getRange(); // quill.getSelection triggers update
                this.dispatchSelectionChangeEvent(range);
            }, 0);
        });

        this.quill.root.addEventListener('focus', this.dispatchFocus);
        this.quill.root.addEventListener('blur', this.dispatchBlur);

        // Prevent dropping so the user can't drag elements around inside
        // the content editable since that breaks formatting
        this.quill.root.addEventListener('drop', preventDefault);

        if (this.options.removeNonFontStyleFormatting) {
            this.quill.root.classList.add('ql-unformatted');
        }
    }

    public focus() {
        this.quill.focus();
    }

    public blur() {
        this.quill.blur();
    }

    public enable(enabled: boolean) {
        this.quill.enable(enabled);
    }

    /**
     * Can be used to set multiple attributes at once with only a single content change being emitted.
     *
     * @remarks We use the silent source to prevent the content change event from being emitted multiple times
     * when each attribute is set and rely on the sync method to emit the single content change event with all of the changed attributes.
     * The main reason we had to do it this way is because Quill's format method does not accept an object of attributes like
     * formatText or formatLine do and we needed it to apply to the user's selection while also respecting that some of the attributes
     * need to be applied to the entire line (e.g. list).
     */
    public setAttributes(attributes: ITextAttributes) {
        for (const attribute of Object.keys(attributes)) {
            this.setAttributeInternal(attribute, attributes[attribute], 'silent');
        }

        // Force Quill to sync its content and emit a content change event with the changed attributes
        this.sync();

        // If the selection is empty we need to reapply the attributes since they can be lost due to the selection getting cleared by the sync above
        const selection = this.quill.getSelection();
        if (selection && selection.length === 0) {
            for (const attribute of Object.keys(attributes)) {
                this.setAttributeInternal(attribute, attributes[attribute], 'silent');
            }
        }
    }

    public setAttribute(attribute: string, value: any) {
        this.setAttributeInternal(attribute, value, 'user');
    }

    private setAttributeInternal(attribute: string, value: any, source: 'api' | 'user' | 'silent' = 'user') {
        // todo: modify assignment to param and assess resulting behavior
        // eslint-disable-next-line no-param-reassign
        value = convertRTFValueToQuill(attribute, value);

        if (CDIFtoQuillAttributeMapping[attribute]) {
            // eslint-disable-next-line no-param-reassign
            attribute = CDIFtoQuillAttributeMapping[attribute];
        }

        this.quill.selection.focus();
        const selection = this.quill.getSelection();

        if ((!selection || !selection.length) && this.options.applyToWholeTextFieldWithNoSelection) {
            // If unformatting a list, we need to make sure we remove the indent as well.
            if (attribute === 'list' && !value) {
                this.quill.formatText(0, this.quill.getLength(), 'indent', false, 'silent');
            }

            this.quill.formatText(0, this.quill.getLength(), attribute, value, source);
        } else {
            // If unformatting a list, we need to make sure we remove the indent as well.
            if (attribute === 'list' && !value) {
                this.quill.format('indent', false, 'silent');
            }

            this.quill.format(attribute, value, source);
        }
    }

    private createAttributeResetObject(): Record<string, null> {
        const context = this.quill.scroll;
        const existingCache = cachedRichTextFields.get(context) || {};
        const keys = Object.keys(existingCache);
        return Object.fromEntries(keys.map((key) => [key, null]));
    }

    public getCommonAttributes() {
        const commonFormats = this.quill.getCommonFormats();
        return mapQuillAttributesToRTF(commonFormats);
    }

    public getAllAttributes() {
        const formats = this.quill.getAllFormats();
        // Map Quill's bullet to CimDoc's unordered attribute
        return mapAllAttributesToRTF(formats);
    }

    public setTextAreaContent(content: TextAreaContent[]) {
        // ideally we would use quill.setContents with a delta, but doing it that way
        // caused quill to casually drop some formatting across newlines, so instead
        // we just insert the text fields one by one.
        this.quill.setText('', 'silent');
        const resetAttributes = this.createAttributeResetObject();

        const convertedFields = convertFieldsToDelta(content);
        convertedFields.forEach((field) => {
            if (field.content.length) {
                const attributes = { ...resetAttributes, ...field.attributes };
                this.quill.insertText(this.quill.getLength() - 1, field.content, attributes, 'silent');
            } else {
                for (const attribute of Object.keys(field.attributes)) {
                    this.quill.formatText(this.quill.getLength() - 1, 1, attribute, field.attributes[attribute], 'silent');
                }
            }
        });

        const lastOp = convertedFields.length !== 0 ? convertedFields[convertedFields.length - 1] : undefined;

        if (lastOp && lastOp.attributes.list) {
            // List ops include a newline, so delete the extra final newline left by Quill
            this.quill.deleteText(this.quill.getLength() - 1, 1, 'silent');
        }

        // this text field was just reset by something outside, so undo doesn't make sense anymore
        this.quill.history.clear();
    }

    public getTextAreaContent() {
        const delta: IDelta = this.quill.getContents();
        return convertDeltaToFields(QuillImplementation.convertColorFromRichText, delta);
    }

    public setSelection(selection: ISelectionRange | null) {
        this.quill.setSelection(selection);
    }

    public getSelection() {
        return this.quill.getSelection();
    }

    /**
     * Retrieves the formatted text content for a specified range within the RichTextField.
     *
     * This method extracts and converts the Quill delta for the given range into
     * **TextAreaContent[]**. If the selection does not end with a block-ending operation (`\n`),
     * it appends the next block-ending op to ensure proper formatting.
     *
     * @param range - The selection range containing the start index and length of the content to extract.
     * @returns An array of `TextAreaContent` representing the formatted text content for the specified range.
     */
    public getRangeTextAreaContent(range: ISelectionRange): TextAreaContent[] {
        const { index, length } = range;
        const delta: IDelta = this.quill.getContents(index, length);
        const remainingDelta = this.quill.getContents(index + length);
        const nextBlockOp = remainingDelta.ops.find(({ insert }) => insert === '\n');

        if (nextBlockOp) {
            delta.ops.push(nextBlockOp);
        }

        return convertDeltaToFields(QuillImplementation.convertColorFromRichText, delta);
    }

    public getLength() {
        return this.quill.getLength();
    }

    public updatePlaceholder(placeholder: string) {
        this.quill.root.setAttribute('data-placeholder', placeholder);
    }

    public insert(text: string, index: number) {
        if (index == null) {
            // eslint-disable-next-line no-param-reassign
            index = this.quill.getLength() - 1;
        }
        this.quill.insertText(index, text);
    }

    public delete(length: number, index: number) {
        if (index == null) {
            // subtracting extra 1 because the trailing newline in quill
            // eslint-disable-next-line no-param-reassign
            index = this.quill.getLength() - 1 - length;
        }
        this.quill.setSelection(index, length);

        // use the keyboard module to do the deletion since it handles managing the zero width space and formatting
        // note that this is using a synthetic event to make that work correctly.
        this.quill.keyboard.handleKeyDown({
            defaultPrevented: false,
            which: 8,
            preventDefault() { this.defaultPrevented = true; },
        });
    }

    /**
     * Some things, like the the converters, need Quill to rerender when they change. This function causes a rerender without the selection being changed.
     */
    public rerender() {
        const selection = this.quill.getSelection();
        const content = this.quill.getContents();
        this.quill.setContents(content, 'silent');
        // We should only update the selection if there is an active selection, otherwise
        // the selection may be taken away from another element as a result of rerendering
        if (selection) {
            this.quill.setSelection(selection, 'silent');
        }
    }

    /**
     * There are instances where we want to force Quill to "resync" its content and also emit a content change event.
     */
    public sync() {
        const selection = this.quill.getSelection();
        const content = this.quill.getContents();
        this.quill.setContents(content, 'api');
        // We should only update the selection if there is an active selection, otherwise
        // the selection may be taken away from another element as a result of rerendering
        if (selection) {
            this.quill.setSelection(selection, 'silent');
        }
    }

    private dispatchSelectionChangeEvent = (range: any) => {
        // don't dispatch if there's no input range
        if (!range) {
            return;
        }

        const attributes = setAttributeFromBlock(this.quill.getFormat(range));

        const convertedAttributes = mapQuillAttributesToRTF(attributes);

        const event = new SelectionChangeEvent(range, convertedAttributes || {});
        this.dispatchEvent(event);
    };

    private dispatchContentChangeEvent = () => {
        const delta: IDelta = this.quill.getContents();
        const textAreaContent = convertDeltaToFields(QuillImplementation.convertColorFromRichText, delta);

        const range = this.quill.selection.getRange();
        this.dispatchSelectionChangeEvent(range[0]);
        const event = new ContentChangeEvent(textAreaContent);
        this.dispatchEvent(event);
    };

    private dispatchFocus = (e) => {
        this.dispatchEvent(new FocusEvent(e));
    };

    private dispatchBlur = (e) => {
        this.dispatchEvent(new BlurEvent(e));
    };
}
